import { ActiveFilters, FilterOption, FilterSection } from '../../../layout/filter-sidebar/filter.model';
import {
  Category,
  CategoryFilters,
  CategoryProductFilters,
  CategorySort,
} from '../../../shared/models/category.model';
import {
  CONDITION_LABELS,
  Product,
  ProductCategoryRef,
  ProductCondition,
} from '../../../shared/models/product.model';
import { CatalogCategory, CatalogSortKey, CategoryPathNode, SubcategoryItem } from './catalog.models';
import { environment } from '../../../../environments/environment';
import { CategoryItem } from '../../home/data/mock-categories';
import { findPathBySlug } from '../../../shared/services/category.service';

// ── Filter section IDs (single source of truth) ─────────────────────────────

/**
 * IDs de secciones de filtro soportadas por `toBackendFilters` y producidas
 * por `toFilterSections`. Usado por `CatalogPageComponent` como whitelist al
 * leer query-params: cualquier clave fuera de este Set no se considera filtro.
 */
export const FILTER_SECTION_IDS: ReadonlySet<string> = new Set([
  'product_type',
  'quality',
  'rarity',
  'variant',
  'expansion',
  'language',
  'availability',
  'price',
]);

const PRODUCT_TYPE_LABELS: Record<string, string> = {
  single: 'Singles',
  sealed: 'Sellados',
  accessory: 'Accesorios',
  board_game: 'Juegos de mesa',
};

// El backend envía el código ISO del idioma (`en`/`es`/`ja`) en el bucket de
// filtros y espera el mismo código de vuelta al filtrar; este map es solo para
// el label visible al usuario. Si crece la lista de idiomas, mejor migrar a un
// pipe i18n o a un campo `display_name` en el bucket.
const LANGUAGE_LABELS: Record<string, string> = {
  en: 'Inglés',
  es: 'Español',
  ja: 'Japonés',
};

// ── Sort ─────────────────────────────────────────────────────────────────────

const UI_TO_UNIFIED_SORT: Record<CatalogSortKey, CategorySort> = {
  'newest': 'newest',
  'price-asc': 'price_asc',
  'price-desc': 'price_desc',
  'name-asc': 'name_asc',
  'name-desc': 'name_desc',
  'popular': 'popular',
};

export function toUnifiedSort(ui: CatalogSortKey): CategorySort {
  return UI_TO_UNIFIED_SORT[ui];
}

// ── Filters (backend buckets → UI sections) ─────────────────────────────────

export function toFilterSections(filters: CategoryFilters | undefined): FilterSection[] {
  if (!filters) return [];

  const sections: FilterSection[] = [];

  if (filters.product_type?.length) {
    sections.push({
      id: 'product_type',
      title: 'Tipo de producto',
      type: 'checkbox',
      options: filters.product_type.map((b) => ({
        value: b.value,
        label: PRODUCT_TYPE_LABELS[b.value] ?? b.value,
        count: b.count,
      })),
    });
  }

  if (filters.quality?.length) {
    sections.push({
      id: 'quality',
      title: 'Condición',
      type: 'checkbox',
      options: filters.quality.map((b) => ({
        value: b.value,
        label: CONDITION_LABELS[b.value as ProductCondition] ?? b.value,
        count: b.count,
      })),
    });
  }

  if (filters.rarity?.length) {
    sections.push({
      id: 'rarity',
      title: 'Rareza',
      type: 'checkbox',
      options: filters.rarity.map(toCheckboxOption),
    });
  }

  const variantOptions: FilterOption[] = [];
  if (filters.is_foil?.count > 0) {
    variantOptions.push({ value: 'foil', label: 'Foil', count: filters.is_foil.count });
  }
  if (filters.is_holo?.count > 0) {
    variantOptions.push({ value: 'holo', label: 'Holo', count: filters.is_holo.count });
  }
  if (variantOptions.length) {
    sections.push({ id: 'variant', title: 'Variante', type: 'checkbox', options: variantOptions });
  }

  if (filters.expansions?.length) {
    sections.push({
      id: 'expansion',
      title: 'Expansión',
      type: 'checkbox',
      options: filters.expansions.map((e) => ({
        value: String(e.id),
        label: e.name,
        count: e.count,
      })),
    });
  }

  if (filters.language?.length) {
    sections.push({
      id: 'language',
      title: 'Idioma',
      type: 'radio',
      options: [
        { value: '', label: 'Todos' },
        ...filters.language.map((b) => ({
          value: b.value,
          label: LANGUAGE_LABELS[b.value] ?? b.value,
          count: b.count,
        })),
      ],
    });
  }

  if (filters.availability) {
    const inStock = filters.availability.in_stock ?? 0;
    const outStock = filters.availability.out_of_stock ?? 0;
    sections.push({
      id: 'availability',
      title: 'Disponibilidad',
      type: 'radio',
      options: [
        { value: '', label: 'Todo' },
        { value: 'in_stock', label: 'En stock', count: inStock },
        { value: 'out_of_stock', label: 'Agotado', count: outStock },
      ],
    });
  }

  if (filters.price_range && (filters.price_range.min !== filters.price_range.max)) {
    sections.push({
      id: 'price',
      title: 'Precio (₡)',
      type: 'range',
      min: Math.floor(filters.price_range.min),
      max: Math.ceil(filters.price_range.max),
      options: [],
    });
  }

  return sections;
}

function toCheckboxOption(b: { value: string; count: number }): FilterOption {
  return { value: b.value, label: b.value, count: b.count };
}

// ── Filters (UI active → backend query) ─────────────────────────────────────

export function toBackendFilters(active: ActiveFilters): Partial<CategoryProductFilters> {
  const out: Partial<CategoryProductFilters> = {};

  for (const [key, value] of Object.entries(active)) {
    if (!value || (Array.isArray(value) && value.length === 0)) continue;
    if (typeof value === 'string' && (value === '' || value === 'all')) continue;

    switch (key) {
      case 'product_type':
      case 'quality':
      case 'language':
      case 'rarity':
        (out as Record<string, unknown>)[key] = value;
        break;

      case 'availability': {
        const v = Array.isArray(value) ? value[0] : value;
        if (v === 'in_stock' || v === 'out_of_stock') out.availability = v;
        break;
      }

      case 'variant': {
        const arr = Array.isArray(value) ? value : [value];
        if (arr.includes('foil')) out.is_foil = true;
        if (arr.includes('holo')) out.is_holo = true;
        break;
      }

      case 'expansion': {
        const arr = Array.isArray(value) ? value : [value];
        const ids = arr.map((v) => Number(v)).filter((n) => Number.isFinite(n));
        if (ids.length) out.expansion = ids;
        break;
      }

      case 'price': {
        const raw = Array.isArray(value) ? value[0] : value;
        if (typeof raw === 'string' && raw.includes('-')) {
          const [minStr, maxStr] = raw.split('-');
          const minTrim = minStr.trim();
          const maxTrim = maxStr.trim();
          if (minTrim !== '') {
            const min = Number(minTrim);
            if (Number.isFinite(min) && min >= 0) out.min_price = min;
          }
          if (maxTrim !== '') {
            const max = Number(maxTrim);
            if (Number.isFinite(max) && max >= 0) out.max_price = max;
          }
        }
        break;
      }
    }
  }

  return out;
}

// ── Category mapping ────────────────────────────────────────────────────────

export function toCatalogCategory(cat: Category): CatalogCategory {
  return {
    slug: cat.slug,
    name: cat.name,
    description: cat.description ?? '',
    productType: null,
    filterSections: [],
    subcategories: toSubcategoryItems(cat.children),
  };
}

export function toSubcategoryItems(
  children: Category[] | undefined,
): SubcategoryItem[] {
  if (!children?.length) return [];
  return children.map((child) => ({
    slug: child.slug,
    name: child.name,
    image: resolveCoverUrl(child.cover),
    link: `/tienda/${child.slug}`,
  }));
}

const FALLBACK_COVER_URL = '/images/hero/broken_hero.jpg';

export function resolveCoverUrl(cover: string | null): string {
  if (!cover) return FALLBACK_COVER_URL;
  if (/^https?:\/\//i.test(cover)) return cover;
  return `${environment.imagesUrl}categories/covers/${cover}`;
}

/**
 * Adapta una `Category` raíz de la API al view-model plano que consume el
 * carrusel del home. La URL queda completa (`/tienda/{slug}`) para que el
 * `[routerLink]` se mantenga simple.
 */
export function mapRootCategoryToCarouselItem(cat: Category): CategoryItem {
  return {
    id: String(cat.id),
    name: cat.name,
    slug: `/tienda/${cat.slug}`,
    image: resolveCoverUrl(cat.cover),
  };
}

// ── Resolución canónica de la categoría de un producto ─────────────────────

/**
 * Devuelve el slug canónico de la categoría top-level de un producto. La API
 * serializa `categories[0]` como la subcategoría más específica con su
 * `parent` raíz; preferimos la raíz (slug del listing principal). El backend
 * garantiza que ambos slugs son autoritativos, así que no hace falta validar
 * contra el árbol. Devuelve `null` sólo si el producto no trae categorías —
 * caso patológico que el caller maneja saltando la navegación.
 */
export function deriveProductCategorySlug(
  product: Pick<Product, 'categories'>,
  _roots?: Category[],
): string | null {
  const cat: ProductCategoryRef | undefined = product.categories?.[0];
  if (!cat) return null;
  return cat.parent?.slug ?? cat.slug ?? null;
}

/**
 * Link a la categoría raíz del producto (juego). Usado para hacer clicable el
 * nombre del juego (`product.game`) en PDP y otros surfaces. Prefiere `parent`
 * sobre la subcategoría — mismo criterio que `deriveProductCategorySlug`.
 */
export function deriveProductRootCategoryLink(
  product: Pick<Product, 'categories'>,
): string | null {
  const cat: ProductCategoryRef | undefined = product.categories?.[0];
  if (!cat) return null;
  const root = cat.parent ?? cat;
  return root.slug ? `/tienda/${root.slug}` : null;
}

/**
 * Link a la subcategoría hoja (set/expansión). Usado para hacer clicable el
 * `setName + setCode` en PDP, quickview modal y featured product block.
 */
export function deriveProductLeafCategoryLink(
  product: Pick<Product, 'categories'>,
): string | null {
  const cat: ProductCategoryRef | undefined = product.categories?.[0];
  return cat?.slug ? `/tienda/${cat.slug}` : null;
}

/**
 * Alias semántico de `deriveProductCategorySlug` que llaman los componentes
 * que construyen URLs a PDP. Se mantiene como función separada (en vez de
 * un re-export) para que cada caller declare su intención: "estoy resolviendo
 * la categoría para una URL de detalle". Devuelve `null` si no hay categoría;
 * el caller decide si filtrar el producto o saltar la navegación.
 */
export function resolvePdpCategorySlug(
  product: Pick<Product, 'categories'>,
  roots?: Category[],
): string | null {
  return deriveProductCategorySlug(product, roots);
}

/**
 * Construye el path jerárquico raíz → hoja para los breadcrumbs de la PDP.
 * Toma el slug canónico del producto y resuelve el camino completo desde la
 * raíz usando `findPathBySlug` contra el árbol cacheado. Devuelve `[]` si
 * no se puede resolver (producto sin categorías o categorías fuera del árbol
 * cacheado — p. ej. SSR antes de hidratar).
 */
export function resolveProductBreadcrumbPath(
  product: Pick<Product, 'categories'>,
  roots: Category[],
): CategoryPathNode[] {
  const slug = deriveProductCategorySlug(product, roots);
  if (!slug) return [];
  const path = findPathBySlug(roots, slug) ?? [];
  return path.map((c) => ({ name: c.name, slug: c.slug }));
}
